// Shared React data hooks for both portals.
import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

export interface ApiQuery<T> {
  /** Last successful result; kept on screen while a reload is in flight. */
  data: T | null;
  /** Message from the last failed load; cleared by the next success. */
  error: string;
  /** True while a load is in flight (including the first). */
  loading: boolean;
  /** Re-run the fetch with the current dependencies. */
  reload: () => void;
  /** Optimistic local updates (e.g. flipping a read flag without a refetch). */
  setData: Dispatch<SetStateAction<T | null>>;
}

/**
 * The load/error/reload triplet every portal page needs: runs `fetcher`
 * whenever `deps` change, keeps the last result during reloads, and drops
 * stale responses when a newer request has since started. Return null from
 * `fetcher` to skip loading (e.g. while the session isn't ready yet).
 */
export function useApiQuery<T>(
  fetcher: () => Promise<T> | null,
  deps: readonly unknown[]
): ApiQuery<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [reloadCount, setReloadCount] = useState(0);
  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  // The latest fetcher, read at effect time so callers can pass an inline
  // closure without it becoming a dependency.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const requestRef = useRef(0);

  useEffect(() => {
    const promise = fetcherRef.current();
    if (!promise) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    promise
      .then((result) => {
        if (requestId !== requestRef.current) return;
        setData(result);
        setError("");
      })
      .catch((err: unknown) => {
        if (requestId !== requestRef.current) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are the caller's fetch inputs
  }, [...deps, reloadCount]);

  return { data, error, loading, reload, setData };
}
